import React, { useState } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { SitrepSectionType } from 'components/DisasterGPT/hooks/useSitrepsV2';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';

type SitrepSectionActionsMenuProps = {
  section: SitrepSectionType;
  onOpenSettings: () => void;
  onDeleteSection: () => void | Promise<void>;
  /**
   * Manually (re-)triggers AI generation for this section. Only invoked for
   * `type: 'ai'` sections.
   */
  onAIGenerate: () => void | Promise<void>;
  isDeleting?: boolean;
  isGeneratingAI?: boolean;
  isLocked?: boolean;
  className?: string;
};

/**
 * Overflow menu for a single SITREP section's toolbar. Groups AI generation,
 * section settings, and section deletion behind a single vertical-ellipsis
 * toggle, replacing the standalone "Section Settings" button.
 */
const SitrepSectionActionsMenu: React.FC<SitrepSectionActionsMenuProps> = ({
  section,
  onOpenSettings,
  onDeleteSection,
  onAIGenerate,
  isDeleting = false,
  isGeneratingAI = false,
  isLocked = false,
  className,
}) => {
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const isAISection = section.type === 'ai';

  const handleAIGenerate = async () => {
    await onAIGenerate();
  };

  const handleConfirmDelete = async () => {
    setShowDeleteConfirmation(false);
    await onDeleteSection();
  };

  return (
    <span
      className={className}
      role="presentation"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="outline"
              size="icon-sm"
              aria-label="Section actions"
              title="Section actions"
            />
          }
        >
          <SharedIcon iconName="more_vert" size="sm" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {isAISection && (
            <DropdownMenuItem
              disabled={isGeneratingAI || isLocked}
              onClick={handleAIGenerate}
            >
              <SharedIcon iconName="auto_awesome" size="sm" />
              AI Generate
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={onOpenSettings}>
            <SharedIcon iconName="settings" size="sm" />
            Section Settings
          </DropdownMenuItem>
          <DropdownMenuItem
            variant="destructive"
            disabled={isDeleting}
            onClick={() => setShowDeleteConfirmation(true)}
          >
            <SharedIcon iconName="delete" size="sm" />
            Delete Section
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <AlertDialog
        open={showDeleteConfirmation}
        onOpenChange={(open) => {
          if (!open) setShowDeleteConfirmation(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Section</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{section.name}&quot;? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => void handleConfirmDelete()}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </span>
  );
};

export default SitrepSectionActionsMenu;
